import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function Resolution() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get data passed from the IncidentView page via the URL
  const runbookId = searchParams.get('runbook_id');
  const moduleIndex = parseInt(searchParams.get('module_index') || '0');
  const incidentNumber = searchParams.get('incident_number');

  // Component State
  const [moduleData, setModuleData] = useState(null);
  const [totalModules, setTotalModules] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Feedback Modal State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [runbookFeedback, setRunbookFeedback] = useState(null);
  const [runbookFeedbackReason, setRunbookFeedbackReason] = useState('');
  const [feedbackError, setFeedbackError] = useState(false);

  useEffect(() => {
    const fetchResolutionData = async () => {
      if (!runbookId) {
        setError("No Runbook ID provided.");
        setLoading(false);
        return;
      }
      try {
        // 1. Fetch the entire runbook to get its list of modules
        // Using /resolution/runbook which fetches from the SAME table as /classify
        const runbookData = await apiCall(`/resolution/runbook/${runbookId}`);
        const modules = runbookData.Modules || [];
        setTotalModules(modules.length);

        // 2. Check if there are more modules to display
        if (moduleIndex < modules.length) {
            // Get the current module's ID
            const currentModuleId = modules[moduleIndex]['Module ID'];
            
            // 3. Fetch the specific module to get its detailed steps
            const detailedModuleData = await apiCall(`/resolution/module/${currentModuleId}`);
            setModuleData(detailedModuleData);
        } else {
            // User has finished all modules for this runbook
            setModuleData(null); 
            setShowFeedbackModal(true); // Automatically open feedback modal
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResolutionData();
  }, [runbookId, moduleIndex]);

  const handleSubmitRunbookFeedback = async () => {
    if (runbookFeedback === null) {
      setFeedbackError(true);
      return;
    }
    setFeedbackError(false);

    // Convert boolean to rating: true (helpful) = 5, false (not helpful) = 1
    const feedbackRating = runbookFeedback === true ? 5 : 1;

    const payload = {
      incident_number: incidentNumber,
      feedback: feedbackRating,
      feedback_reason: runbookFeedbackReason,
    };

    console.log('Submitting overall feedback:', payload);

    try {
      const response = await apiCall('/submit_feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('Overall feedback response:', response);
      alert("Thank you for your feedback!");
      navigate("/incident_view");
    } catch (error) {
      console.error('Overall feedback error:', error);
      alert(`Failed to submit feedback: ${error.message}`);
    }
  };

  const handleStepFeedback = async (rowIndex, feedbackValue) => {
    if (!moduleData || feedbackValue === "") return;

    const step = moduleData.Steps[rowIndex];
    const reasonInput = document.getElementById(`reason-${rowIndex}`);
    const reason = reasonInput ? reasonInput.value : "";

    // Use step index (0, 1, 2...) as step_number for Lambda
    // The actual step label (2.1, 2.3, etc.) is stored in step['Step Number']
    const payload = {
      incident_number: incidentNumber,
      runbook_number: runbookId,
      module_index: moduleIndex,
      step_number: rowIndex, // Use index (0-based) instead of step label
      step_label: step['Step Number'], // Keep original label for reference
      feedback: feedbackValue === 'useful' ? 1 : 0,
      feedback_reason: reason,
    };

    console.log('Submitting step feedback:', payload);

    try {
      const response = await apiCall('/submit_step_feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('Step feedback response:', response);
      // Feedback submitted silently - check console for confirmation
    } catch (error) {
      console.error('Step feedback error:', error);
      // Only show alert on error
      alert(`Failed to submit feedback: ${error.message}`);
    }
  };

  if (loading) return <div className="container mt-4">Loading resolution steps...</div>;
  if (error) return <div className="container mt-4 alert alert-danger">{error}</div>;

  // Render the steps table if there is a module, otherwise show nothing (modal will handle the view)
  return (
    <div className="container mt-4">
      {moduleData && (
        <div>
          <h1>Resolution Steps for Incident {incidentNumber}</h1>
          <div className="card p-3">
            <h2>Module {moduleIndex + 1} of {totalModules}: {moduleData['Module Description']}</h2>
            <table className="table table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Step #</th>
                  <th>Step Details</th>
                  <th>URL</th>
                  <th>Feedback</th>
                  <th>Reason (if not useful)</th>
                </tr>
              </thead>
              <tbody>
                {moduleData.Steps.map((step, index) => (
                  <tr key={index}>
                    <td>{step['Step Number']}</td>
                    <td>{step['Description']}</td>
                    <td>
                      {step['URL'] ? <a href={step['URL']} target="_blank" rel="noopener noreferrer">Link</a> : "N/A"}
                    </td>
                    <td>
                      <select className="form-select" onChange={(e) => handleStepFeedback(index, e.target.value)}>
                        <option value="">Select</option>
                        <option value="useful">Useful</option>
                        <option value="not_useful">Not Useful</option>
                      </select>
                    </td>
                    <td><input type="text" className="form-control" id={`reason-${index}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/resolution?runbook_id=${runbookId}&module_index=${moduleIndex - 1}&incident_number=${incidentNumber}`)}
              disabled={moduleIndex === 0}
            >
              Previous Module
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/resolution?runbook_id=${runbookId}&module_index=${moduleIndex + 1}&incident_number=${incidentNumber}`)}
            >
              {moduleIndex + 1 < totalModules ? 'Next Module' : 'Done'}
            </button>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="feedback-modal-overlay">
          <div className="feedback-modal-content">
            <div className="feedback-modal-header">
              <h3 className="feedback-modal-title">Was this runbook helpful?</h3>
            </div>
            <div className="feedback-modal-body">
              <div className="feedback-option">
                <input
                  type="radio"
                  id="feedbackYes"
                  name="feedback"
                  value="true"
                  checked={runbookFeedback === true}
                  onChange={() => setRunbookFeedback(true)}
                />
                <label htmlFor="feedbackYes">Yes</label>
              </div>
              <div className="feedback-option">
                <input
                  type="radio"
                  id="feedbackNo"
                  name="feedback"
                  value="false"
                  checked={runbookFeedback === false}
                  onChange={() => setRunbookFeedback(false)}
                />
                <label htmlFor="feedbackNo">No</label>
              </div>

              {feedbackError && (
                <div className="feedback-error">
                  Please select a feedback option (Yes or No).
                </div>
              )}

              {runbookFeedback === false && (
                <textarea
                  name="feedback_reason"
                  id="feedback_reason"
                  placeholder="Please let us know why it wasn't helpful..."
                  className="feedback-reason-box"
                  value={runbookFeedbackReason}
                  onChange={(e) => setRunbookFeedbackReason(e.target.value)}
                ></textarea>
              )}
            </div>
            <div className="feedback-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSubmitRunbookFeedback}>Submit Feedback</button>
            </div>
          </div>
        </div>
      )}
      {/* ============== END: UPDATED MODAL JSX ============== */}
    </div>
  );
}

export default Resolution;
